import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  getActionLabel,
  getAllowedActions,
  getStatusLabel,
  getTypeLabel,
  isPersonalBlock,
  STATUS_VARIANTS,
} from "../../lib/appointmentStatus";
import {
  formatAppointmentTime,
  getAppointmentDurationMinutes,
  resolveBrowserTimezone,
} from "../../lib/agendaDateTime";

function placeSummary(apt) {
  const bits = [apt.institution, apt.room, apt.address].filter(Boolean);
  return bits.join(" · ") || null;
}

export default function AppointmentCard({
  appointment,
  patientLabel,
  patientArchived = false,
  english = false,
  showcaseMode = false,
  onAction,
  locale = "es-GT",
  timeZone = resolveBrowserTimezone(),
}) {
  const apt = appointment || {};
  const personal = isPersonalBlock(apt.appointment_type);
  const actions = getAllowedActions(apt.status, apt.appointment_type).filter((id) => {
    if (id === "open_record" || id === "start_consultation") {
      return Boolean(apt.patient_id) && !patientArchived && !showcaseMode;
    }
    return true;
  });
  const visibleActions = showcaseMode
    ? actions.filter((id) => ["confirm", "complete", "cancel_clinic", "edit"].includes(id)).slice(0, 3)
    : actions;

  const startLabel = formatAppointmentTime(apt.start_at, locale, timeZone);
  const endLabel = formatAppointmentTime(apt.end_at, locale, timeZone);
  const duration = getAppointmentDurationMinutes(apt.start_at, apt.end_at);
  const buffer =
    (Number(apt.buffer_before_minutes) || 0) + (Number(apt.buffer_after_minutes) || 0);
  const place = placeSummary(apt);
  const title = personal
    ? apt.reason_short || (english ? "Personal block" : "Bloqueo personal")
    : patientLabel || (english ? "Patient" : "Paciente");

  return (
    <article
      className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
      data-testid="appointment-card"
      data-appointment-id={apt.id}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-base font-semibold text-slate-900">
              {startLabel}
              <span className="font-normal text-slate-500"> – {endLabel}</span>
            </p>
            <Badge
              variant="outline"
              className={STATUS_VARIANTS[apt.status] || STATUS_VARIANTS.scheduled}
              aria-label={`${english ? "Status" : "Estado"}: ${getStatusLabel(apt.status, { english })}`}
            >
              {getStatusLabel(apt.status, { english })}
            </Badge>
          </div>
          <p className="truncate text-sm font-medium text-slate-800">{title}</p>
          {patientArchived && !personal ? (
            <p className="text-xs font-medium text-amber-800">
              {english ? "Archived patient" : "Paciente archivado"}
            </p>
          ) : null}
          <p className="text-sm text-slate-600">
            {[getTypeLabel(apt.appointment_type, { english }), duration ? `${duration} min` : null]
              .filter(Boolean)
              .join(" · ")}
          </p>
          {apt.reason_short && !personal ? (
            <p className="text-sm text-slate-600">{apt.reason_short}</p>
          ) : null}
          {place ? <p className="text-xs text-slate-500">{place}</p> : null}
          {buffer > 0 ? (
            <p className="text-xs text-slate-500">
              {english ? `Buffer / travel: ${buffer} min` : `Buffer / traslado: ${buffer} min`}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2 sm:max-w-[280px] sm:justify-end">
          {visibleActions.map((actionId) => (
            <Button
              key={actionId}
              type="button"
              size="sm"
              variant={actionId.startsWith("cancel") || actionId === "no_show" ? "outline" : "secondary"}
              className="min-h-10"
              onClick={() => onAction?.(actionId, apt)}
              data-testid={`appointment-action-${actionId}`}
            >
              {getActionLabel(actionId, { english })}
            </Button>
          ))}
        </div>
      </div>
    </article>
  );
}
